<template>
  <div class="livestream p-relative h-100">
    <template v-if="isBuilderContext()">
      <block-placeholder icon="live_tv" :text="placeholderText" />
    </template>
    <template v-else>
      <video-embed
        :video="video"
        auto-initialize
        portrait
        hide-controls
        :hide-play-button="hasInteractedWithVideo"
        @play="handlePlay"
        @pause="handlePause"
        @ready="handlePlayerReady"
      />

      <div
        :class="[
          {
            '--allow-clickthrough': !hasInteractedWithVideo,
            '--paused': hasInteractedWithVideo && !this.isPlaying
          },
          'livestream-video__overlay p-overlay z-3'
        ]"
      >
        <template v-if="hasInteractedWithVideo">
          <div
            class="livestream-video__controls h-100 w-100"
            @click="togglePlayback"
          />
          <div class="livestream-video__chat">
            <message-thread
              :message-thread-id="messageThreadId"
              overlay
              with-reactions
              with-participants
              auto-hide
              :with-notifications="false"
              @click="() => videoPlayer.play()"
            />
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions } from "vuex"

import { ACTIVITY_TYPES } from "@/store/analytics"

import { isBuilderContext, capitalizeFirstLetter } from "@/lib/utils"

import MessageThread from "@/components/message-thread/Thread"
import VideoEmbed from "@/components/VideoEmbed"

export default {
  components: {
    MessageThread,
    VideoEmbed,
    BlockPlaceholder: () => import("@/components/stream/BlockPlaceholder")
  },

  props: {
    id: { type: [String, Number], required: true },
    remoteId: { type: [String, Number], required: true },
    provider: { type: String, required: true },
    video: { type: Object, required: true },
    messageThreadId: { type: [Number, String], required: true }
  },

  data() {
    return {
      hasTrackedJoin: false,
      hasInteractedWithVideo: false,
      isPlaying: false,
      isLoading: true,
      videoPlayer: null
    }
  },

  computed: {
    placeholderText() {
      return `${capitalizeFirstLetter(this.video.provider)} Stream`
    },

    activityData() {
      return {
        livestream: {
          id: this.id,
          provider: this.provider,
          remote_id: this.remoteId
        }
      }
    }
  },

  methods: {
    isBuilderContext,
    capitalizeFirstLetter,

    ...mapActions("analytics", ["trackActivity"]),

    handlePlay() {
      this.hasInteractedWithVideo = true
      this.isPlaying = true

      if (!this.hasTrackedJoin) {
        this.trackActivity({
          activityType: ACTIVITY_TYPES.LIVESTREAM_JOINED,
          data: this.activityData
        }).then(() => {
          this.hasTrackedJoin = true
        })
      }
    },

    handlePause() {
      this.isPlaying = false
    },

    handlePlayerReady(player) {
      this.videoPlayer = player
      this.isLoading = false
    },

    togglePlayback() {
      if (this.isPlaying) {
        this.videoPlayer.pause()
      } else {
        this.videoPlayer.play()
      }
    }
  },

  deactivated() {
    this.trackActivity({
      activityType: ACTIVITY_TYPES.LIVESTREAM_LEFT,
      data: this.activityData
    })
    this.hasTrackedJoin = false
    this.hasInteractedWithVideo = false
    this.isPlaying = false
    this.isLoading = true
  },

  beforeDestroy() {
    this.trackActivity({
      activityType: ACTIVITY_TYPES.LIVESTREAM_LEFT,
      data: this.activityData
    })
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.livestream-video {
  min-height: 25vh;
}

.livestream-video__overlay {
  display: grid;
  grid-template-rows: 40% 60%;
  grid-template-columns: 1fr;
  transition: background 0.2s ease;

  &.--allow-clickthrough {
    pointer-events: none;
  }

  &.--paused {
    background: rgba($color-rgb--black, 0.24);
  }
}

.livestream-video__chat {
  @include hide-scroll-bars;

  max-height: 100%;
  height: 100%;
  min-height: 400px;
}
</style>

<style lang="scss">
.livestream {
  .video-embed__video.--playing.--has-interacted {
    [data-plyr="play"] {
      top: 20%;
    }
  }
}
</style>
